'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  deleteDocument,
  getDocument,
  listDocuments,
  newDocumentId,
  saveDocument,
  type DocumentKind,
  type StoredDocument,
} from '@/lib/tools-storage';

/** Через сколько миллисекунд простоя сохраняем черновик. */
const SAVE_DELAY = 600;

/** Заголовок документа для списка недавних: первая непустая строка. */
function titleOf(content: string, fallback: string): string {
  const line = content
    .split('\n')
    .map((s) => s.replace(/^#+\s*/, '').trim())
    .find((s) => s.length > 0);

  if (!line) return fallback;
  return line.length > 60 ? `${line.slice(0, 60)}…` : line;
}

/**
 * Документ редактора и история недавних.
 *
 * Сохранение отложенное: писать в IndexedDB на каждое нажатие клавиши
 * незачем, а терять работу при закрытии вкладки нельзя, поэтому черновик
 * дополнительно сбрасывается на `pagehide`.
 *
 * Первый документ не создаётся в хранилище заранее: пустая запись в списке
 * недавних после случайного захода на страницу только мешает. Запись
 * появляется, когда в редакторе есть текст.
 */
export function useToolDocument(kind: DocumentKind, untitled: string) {
  const [id, setId] = useState<string>(() => newDocumentId());
  const [content, setContent] = useState('');
  const [recent, setRecent] = useState<StoredDocument[]>([]);
  const [saving, setSaving] = useState(false);

  // Актуальное состояние для обработчика pagehide: он вешается один раз,
  // и без ref увидел бы значения первого рендера.
  const latest = useRef({ id, content });
  latest.current = { id, content };

  const refresh = useCallback(() => {
    void listDocuments(kind).then(setRecent);
  }, [kind]);

  useEffect(refresh, [refresh]);

  const persist = useCallback(
    async (docId: string, text: string) => {
      if (!text.trim()) return;

      await saveDocument({
        id: docId,
        kind,
        title: titleOf(text, untitled),
        content: text,
        updatedAt: Date.now(),
      });
    },
    [kind, untitled],
  );

  // Отложенное сохранение.
  useEffect(() => {
    if (!content.trim()) return;

    setSaving(true);
    const timer = setTimeout(() => {
      void persist(id, content).then(() => {
        setSaving(false);
        refresh();
      });
    }, SAVE_DELAY);

    return () => {
      clearTimeout(timer);
    };
  }, [content, id, persist, refresh]);

  // Закрытие вкладки не должно стоить последних набранных строк.
  useEffect(() => {
    const onHide = () => {
      void persist(latest.current.id, latest.current.content);
    };

    window.addEventListener('pagehide', onHide);
    return () => {
      window.removeEventListener('pagehide', onHide);
    };
  }, [persist]);

  /** Открывает документ из списка недавних. */
  const open = useCallback((doc: StoredDocument) => {
    setId(doc.id);
    setContent(doc.content);
  }, []);

  /** Открывает документ по идентификатору, например из адреса общей ссылки. */
  const openById = useCallback(async (docId: string) => {
    const doc = await getDocument(docId);
    if (!doc) return false;

    setId(doc.id);
    setContent(doc.content);
    return true;
  }, []);

  /** Начинает новый документ, не трогая предыдущий. */
  const create = useCallback(() => {
    setId(newDocumentId());
    setContent('');
  }, []);

  /** Убирает документ из списка недавних. */
  const remove = useCallback(
    async (docId: string) => {
      await deleteDocument(docId);
      // Удалили тот, что открыт: оставлять его на экране без записи
      // в истории честно, но продолжать в него писать некуда.
      if (docId === latest.current.id) create();
      refresh();
    },
    [create, refresh],
  );

  return { id, content, setContent, recent, saving, open, openById, create, remove, setId };
}
